// The Academy tab under Drivers: who is in the Academy, where each person
// stands, who Fleetly is sponsoring, and what has been paid for them. Pure
// functions, no JSX, so Node's own test runner can import them.
//
// NO STAGE IS WORKED OUT HERE. Until 30 September 2026 this file decided where
// a person stood (standing()), which CLAUDE.md rule 4 forbids, and it put every
// failed interview, no-show and withdrawal under the same blue "Applied" as
// the people in interviews. academy_stage_v2 in Postgres (db/migrations/0367b)
// now decides, and this file only filters what it was given.
import type { ChipTone } from '../components/interviews/Chip'

export type StageKey =
  | 'new' | 'invited' | 'applied' | 'interviews' | 'onboarding' | 'driver' | 'not_taken_forward' | 'left'

export type AcademyMember = {
  intake_id: string
  person_id: string
  full_name: string | null
  primary_email: string | null
  primary_phone: string | null
  whatsapp_phone: string | null
  province: string | null
  town: string | null
  enrolled_at: string | null
  dropped_out_at: string | null
  dropped_out_reason: string | null
  notes: string | null
  source_label: string | null
  has_uber_profile: boolean | null
  latest_application_id: string | null
  latest_application_status: string | null
  latest_application_at: string | null
  prdp_verdict: string | null
  prdp_expiry: string | null
  apply_invite_sent_at: string | null
  is_driver: boolean | null
  stage: StageKey
  stage_order: number
  stage_label: string
  stage_tone: ChipTone
  sponsored: boolean
  sponsored_at: string | null
  sponsored_note: string | null
  sponsored_by_name: string | null
  requested_zar: number
  paid_zar: number
  open_requests: number
  to_pay: number
  recoverable_zar: number
}

export type AcademyBoard = {
  can_request: boolean
  can_release: boolean
  can_recover: boolean
  stages: { key: StageKey; label: string; count: number }[]
  sponsored_count: number
  to_pay_count: number
  members: AcademyMember[]
}

export type RequestKind = { code: string; group: string; label: string; needs_detail: boolean }

export type RequestOptions = { kinds: RequestKind[]; banks: string[]; recovery_instalments: number }

export type AcademyRequest = {
  id: string
  ref: string
  status: 'sending' | 'send_failed' | 'awaiting_signature' | 'signed' | 'declined' | 'cancelled'
  total_zar: number
  note: string | null
  items: { label: string; amount_zar: number; detail: string | null }[]
  payout_method: 'ewallet' | 'bank'
  payout_text: string
  requested_by_name: string
  requested_at: string
  sent_at: string | null
  signer_email: string | null
  send_error: string | null
  signed_at: string | null
  declined_at: string | null
  last_checked_at: string | null
  notified_at: string | null
  paid_out_at: string | null
  paid_out_method: 'marked' | 'emailed_proof' | null
  paid_out_note: string | null
  paid_out_by_name: string | null
  cancelled_at: string | null
  cancel_reason: string | null
  converted_loan_ref: string | null
  has_document: boolean
  proofs: { id: string; filename: string | null; received_at: string | null; from_email: string | null }[]
}

/** 'all', or one stage. */
export type AcademyView = 'all' | StageKey

export type AcademyFlags = { sponsored: boolean; toPay: boolean; outsideGauteng: boolean }

export function isGauteng(m: Pick<AcademyMember, 'province'>): boolean {
  return (m.province ?? '').trim().toLowerCase() === 'gauteng'
}

export function matchesView(m: AcademyMember, view: AcademyView, flags: AcademyFlags): boolean {
  if (view !== 'all' && m.stage !== view) return false
  if (flags.sponsored && !m.sponsored) return false
  if (flags.toPay && !(m.to_pay > 0)) return false
  if (flags.outsideGauteng && isGauteng(m)) return false
  return true
}

export function matchesSearch(
  m: Pick<AcademyMember, 'full_name' | 'primary_email' | 'town' | 'primary_phone' | 'whatsapp_phone'>,
  term: string,
): boolean {
  const t = term.trim().toLowerCase()
  if (!t) return true
  const digits = t.replace(/\D/g, '')
  const hay = [m.full_name, m.primary_email, m.town].filter(Boolean).join(' ').toLowerCase()
  if (hay.includes(t)) return true
  if (digits.length >= 6) {
    const phones = [m.primary_phone, m.whatsapp_phone].map((p) => (p ?? '').replace(/\D/g, ''))
    return phones.some((p) => p.endsWith(digits.slice(-9)))
  }
  return false
}

export const REQUEST_STATUS: Record<AcademyRequest['status'], { label: string; tone: ChipTone }> = {
  sending: { label: 'Sending', tone: 'amber' },
  send_failed: { label: 'Not sent', tone: 'red' },
  awaiting_signature: { label: 'Waiting for their signature', tone: 'amber' },
  signed: { label: 'Signed', tone: 'green' },
  declined: { label: 'Declined', tone: 'red' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
}
